import NetworkService from "@/services/NetworkService";
import moment from "moment";
import PayoutService from "./PayoutService";

const periodLabel = {
  month: "Mensal",
  semester: "Semestral",
  year: "Anual",
};

const invoiceStatus = {
  uncollectible: "Cobrança não aplicável",
  draft: "Rascunho",
  open: "Em aberto",
  void: "Cancelado",
  paid: "Pago",
};

const subscriptionStatus = {
  incomplete: "Problema de pagamento",
  incomplete_expired: "Cancelado (expirado)",
  trialing: "Período de teste",
  active: "Ativo",
  past_due: "Pagamento atrasado",
  canceled: "Cancelado",
  unpaid: "Não pago",
  paused: "Pausado",
};

export default class UserService {
  constructor() {
    this.network = new NetworkService();
    this.payoutService = new PayoutService();
  }
  SUBSCRIPTION_STATUS = {
    trialing: "trialing",
    active: "active",
  };

  static PlanSlugs = {
    plus: "psiapp-mais-pro",
    basic: "psiapp-membro-pro",
    free: "free-admin-premium",
  };

  planLabels = {
    [UserService.PlanSlugs.plus]: "Plano Plus",
    [UserService.PlanSlugs.basic]: "Plano Básico",
    [UserService.PlanSlugs.free]: "Plano Gratuito",
  };
  list = (filters) => {
    return this.network.get(this.network.endpoints.users, filters);
  };
  create = (params) => {
    return this.network.post(this.network.endpoints.users, params);
  };
  updatePicture = (id, params) => {
    return this.network.postMultipart(
      `${this.network.endpoints.users}/${id}/edit`,
      params
    );
  };
  update = (id, params) => {
    return this.network.put(`${this.network.endpoints.users}/${id}`, params);
  };

  reactivateUser = (user_id) => {
    return this.network.post(`${this.network.endpoints.reactivateUser}`, {
      user_id: user_id,
    });
  };

  show = (id) => {
    return this.network.get(`${this.network.endpoints.users}/${id}`);
  };
  delete = (id) => {
    return this.network.delete(`${this.network.endpoints.users}/${id}`);
  };
  settings = (key) => {
    return this.network.get(`api/${key}`);
  };
  updateSettings = (key, value) => {
    return this.network.put(`api/${key}`, { terms_of_use: value });
  };
  login = async (params) => {
    let res = await this.network.post(this.network.endpoints.login, params);
    if (["admin", "specialist"].includes(res.data.type)) {
      return res;
    } else {
      throw { status: false, message: "Credenciais inválidas" };
    }
  };
  logout = () => {
    return this.network.put(`${this.network.endpoints.users}/logout`);
  };
  listStatistics = (params) => {
    return this.network.get(this.network.endpoints.summary, params);
  };
  listAppointmentGraphics = (params) => {
    return this.network.get(this.network.endpoints.appointmentGraphics, params);
  };
  createWorkingHours(params) {
    return this.network.post(this.network.endpoints.workingHours, params);
  }
  listCrashes = (params) => {
    return this.network.get(this.network.endpoints.appCrashes, params);
  };
  showCrash = (id) => {
    return this.network.get(`${this.network.endpoints.appCrashes}/${id}`);
  };
  exportUsers(params) {
    return this.network.post(this.network.endpoints.exportUsers, params);
  }
  givePremium(user_id) {
    return this.network.post(this.network.endpoints.givePremium, { user_id });
  }
  removePremium(user_id) {
    return this.network.post(this.network.endpoints.removePremium, { user_id });
  }
  listCreditCards(user_id) {
    return this.network.get(this.network.endpoints.creditCards, { user_id });
  }
  listUserChangeLogs(userId, params = {}) {
    return this.network.get(
      `${this.network.endpoints.userChangeLogs}/users/${userId}`,
      params
    );
  }
  preparePsi(item) {
    //qdo precisar formatar o psi, usa essa funcao
    try {
      item.formatted_birthdate = moment(item.birth_date).format("DD/MM/YYYY");
      item.formatted_created_at = moment(item.created_at).format(
        "DD/MM/YYYY [às] HH:mm"
      );

      item.formatted_gender = item.gender == "male" ? "Masculino" : "Feminino";
      item.formatted_price = item.appointment_price
        ? item.appointment_price.toCurrency()
        : "";
      item.isPremium = false;

      if (item.subscription) {
        const status = item.subscription?.status;
        const trialEnd = item.subscription?.trial_end;
        const cancelAt = item.subscription?.cancel_at;
        const slug = item.subscription?.plan?.slug;

        const isTrialingCanceledStillValid =
          status === "canceled" &&
          trialEnd &&
          moment(trialEnd).isAfter(moment());

        item.is_subscriber = !!(
          item.subscription &&
          (status === "active" ||
            status === "trialing" ||
            isTrialingCanceledStillValid)
        );

        item.isPremium = item.is_subscriber;
      }

      if (item.payouts?.length) {
        item.payouts.map((elem) => {
          elem.formatted_payout = this.payoutService.preparePayout(elem);
        });
      }
      if (item.latest_ratings?.length) {
        item.latest_ratings = item.latest_ratings.filter(
          (elem) => elem.user != null
        );
      }
      item.callProviderLabel =
        item.call_provider == "agora" ? "Agora" : "Whereby";

      if (item.subscriptions && item.subscriptions?.length) {
        item.subscriptions = item.subscriptions.map((sub) => {
          sub.formatted_start_date = moment(sub.start_date).format(
            "DD/MM/YYYY"
          );
          sub.formatted_canceled_at = sub.canceled_at
            ? moment(sub.canceled_at).format("DD/MM/YYYY")
            : "---";
          sub.formatted_current_period_start = moment(
            sub.current_period_start
          ).format("DD/MM/YYYY");
          sub.formatted_current_period_end = moment(
            sub.current_period_end
          ).format("DD/MM/YYYY");
          sub.formatted_unit_amount = sub.unit_amount
            ? (sub.unit_amount / 100).toCurrency()
            : "";

          sub.formatted_status =
            subscriptionStatus[sub.status] || sub.status || "---";
          if (
            sub.status === "canceled" &&
            sub.trial_end &&
            moment(sub.trial_end).isAfter(moment())
          ) {
            sub.formatted_status = `Cortesia até ${moment(sub.trial_end).format(
              "DD/MM/YYYY"
            )}`;
          } else if (sub.status === "active" && sub.canceled_at) {
            sub.formatted_status = `Ativo até ${moment(sub.cancel_at).format(
              "DD/MM/YYYY"
            )}`;
          } else if (sub.status === "trialing" && sub.trial_end) {
            sub.formatted_status = `Período de teste até ${moment(
              sub.trial_end
            ).format("DD/MM/YYYY")}`;
          }

          sub.formatted_plan_frequency = "---";
          if (sub.price && sub.price.frequency) {
            sub.formatted_plan_frequency = `${
              this.planLabels[sub?.plan?.slug]
            } ${periodLabel[sub.price.frequency]}`;
          }

          if (sub.invoices && sub.invoices.length) {
            sub.invoices = sub.invoices.map((inv) => {
              inv.formatted_created = moment(inv.created).format("DD/MM/YYYY");
              inv.formatted_status = inv.status
                ? invoiceStatus[inv.status]
                : "";

              inv.formatted_total = inv.total ? inv.total.toCurrency() : "";
              return inv;
            });
          }
          return sub;
        });
      }
      return item;
    } catch (e) {
      console.log(e);
    }
  }
  prepareUser(item) {
    //qdo precisar formatar o psi, usa essa funcao
    try {
      item.formatted_created_at = moment(item.created_at).format(
        "DD/MM/YYYY [às] HH:mm"
      );
      return item;
    } catch (e) {
      console.log(e);
    }
  }
}
